import React, { useState, useEffect, useRef } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import { formatDate } from '../utils/dateUtils';
import { getBalance, updateBalance } from '../services/BankAccountService';
import { sanitizeNumeric } from '../utils/sanitize';
import { debugLog, debugSensitive, logError } from '../utils/debugUtils';

const UpdateBalance = ({ onUpdate, bankAccountId = null }) => {
  const [balAmount, setBalance] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // Fetch the current balance from the BankAccount API and update the state
    if (bankAccountId) {
      getBalance(bankAccountId)
        .then((balanceAmount) => {
          // Extract just the amount string from the balance object
          const amount = balanceAmount?.amount || '';
          debugSensitive('Balance fetched', amount);
          setBalance(amount);
          requestAnimationFrame(() => {
            if (inputRef.current) {
              inputRef.current.focus();
              inputRef.current.select();
            }
          });
        })
        .catch((error) => {
          // Handle the error
          logError('fetching balance', error);
          setBalance('');
        });
    }
  }, [bankAccountId]); // Re-fetch when bankAccountId changes

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    // Debug the input value
    debugSensitive('Form submitted with balAmount', balAmount);
    debugLog('Type of balAmount:', typeof balAmount);
    debugSensitive('Bank Account ID', bankAccountId);

    if (!bankAccountId) {
      debugLog('No bank account ID provided');
      setLoading(false);
      alert('Please select an account and try again.');
      return;
    }

    // Clean and validate the input amount
    const cleanAmount = sanitizeNumeric(balAmount);
    const parsedAmount = parseFloat(cleanAmount);
    
    debugSensitive('Clean amount', cleanAmount);
    debugSensitive('Parsed amount', parsedAmount);

    // Validate the amount
    if (isNaN(parsedAmount) || parsedAmount < 0) {
      debugSensitive('Invalid amount entered', balAmount);
      setLoading(false);
      alert('Please check your input and try again.');
      return;
    }

    const data = {
      amount: parsedAmount.toFixed(2), // Ensure 2 decimal places
      update_date: formatDate(new Date()),
      bank_account_id: bankAccountId
    };

    debugSensitive('Data being sent to BankAccount API', data);

    // Call the updateBalance function to update the bank account's balance field
    updateBalance(bankAccountId, data)
      .then((response) => {
        debugSensitive('BankAccount API response', response);
        onUpdate(response.amount); // Call the onUpdate function with the updated balance
        // Update the form to show the new balance value
        setBalance(response.amount);
        setLoading(false);
      })
      .catch((error) => {
        // Handle the error
        logError('updating balance', error);
        setLoading(false);
        alert('Please try again.');
        throw error;
      });
  };

  const handleBalanceChange = (e) => {
    // Get the raw input value
    const rawValue = e.target.value;
    debugSensitive('Input changed - raw value', rawValue);
    
    // For numeric fields, we want to allow only numbers and decimals
    const sanitized = sanitizeNumeric(rawValue);
    debugSensitive('Input changed - sanitized value', sanitized);
    
    setBalance(sanitized);
  };

  return (
    <Form onSubmit={handleFormSubmit}>
      <Form.Group className="mb-3" controlId="balAmount">
        <Form.Label>Update Balance{bankAccountId ? ' for this bank account' : ''}:</Form.Label>
        <InputGroup>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control
            ref={inputRef}
            type="text"
            value={balAmount}
            onChange={handleBalanceChange}
            onFocus={(e) => e.target.select()}
            placeholder="Enter current balance"
            disabled={loading}
            inputMode="decimal"
            pattern="[0-9]*\.?[0-9]*"
            autoFocus
          />
        </InputGroup>
      </Form.Group>

      <Button variant="primary" type="submit" disabled={loading}>
        {loading ? 'Updating...' : 'Update'}
      </Button>
    </Form>
  );
};

export default UpdateBalance;
